import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import { categories, allCategories } from "@/mocks/categories";
import { getComicsByCategory } from "@/mocks/comics";
import ComicCard from "@/components/base/ComicCard";

const slugMeta: Record<string, { eyebrow: string; desc: string }> = {
  fantasy: { eyebrow: "异世界 · 魔法 · 热血冒险", desc: "韩漫大全奇幻专题持续推荐异世界冒险、魔法学院、龙族重生与超能力生存游戏等热血佳作。" },
  campus: { eyebrow: "青春 · 恋爱 · 日常成长", desc: "韩漫大全校园专题收录青春恋爱、社团日常与青春成长类佳作，甜度与温度并存。" },
  ancient: { eyebrow: "宫廷 · 玄幻 · 古装爱恋", desc: "韩漫大全古风专题汇集宫廷权谋、仙侠玄幻与古装爱恋，华美画风演绎东方意境。" },
  all: { eyebrow: "全部题材 · 一站浏览", desc: "浏览韩国漫画大全收录的全部韩漫佳作，覆盖奇幻、校园、古风等各类题材。" },
};

function CategoryPage() {
  const { slug = "all" } = useParams();
  const category = allCategories.find((c) => c.slug === slug);
  const meta = slugMeta[slug] ?? slugMeta.all;

  const comics = useMemo(() => getComicsByCategory(slug), [slug]);

  const [tag, setTag] = useState<string>("全部");
  const tagPool = useMemo(() => {
    const s = new Set<string>();
    comics.forEach((c) => c.tags.forEach((t) => s.add(t)));
    return ["全部", ...Array.from(s)];
  }, [comics]);

  const filtered = useMemo(
    () => (tag === "全部" ? comics : comics.filter((c) => c.tags.includes(tag))),
    [comics, tag]
  );

  return (
    <Layout>
      <section className="relative overflow-hidden bg-foreground-950 py-24">
        <div
          className="pointer-events-none absolute inset-0 opacity-25"
          style={{
            backgroundImage:
              "radial-gradient(circle at 25% 20%, oklch(var(--primary-500) / 0.6), transparent 45%), radial-gradient(circle at 80% 75%, oklch(var(--accent-500) / 0.5), transparent 45%)",
          }}
          aria-hidden="true"
        />
        <div className="relative mx-auto max-w-7xl px-4 md:px-6">
          <nav aria-label="面包屑" className="flex items-center gap-2 text-sm text-background-400">
            <Link to="/" className="transition-colors hover:text-accent-300">首页</Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true" />
            <span className="text-background-200">{category?.name ?? "专题"}</span>
          </nav>
          <div className="mt-6 flex items-center gap-4">
            <span className={`flex h-14 w-14 items-center justify-center rounded-lg ${category?.accent === "primary" ? "bg-primary-500" : category?.accent === "accent" ? "bg-accent-500" : "bg-secondary-500"} text-background-50`}>
              <i className={`${category?.icon ?? "ri-archive-2-fill"} text-2xl`} aria-hidden="true" />
            </span>
            <div>
              <h1 className="font-heading text-3xl font-bold text-background-50 md:text-4xl">
                韩国漫画大全 · {category?.name ?? "全部作品"}
              </h1>
              <p className="mt-1 text-sm font-medium text-accent-300">{meta.eyebrow}</p>
            </div>
          </div>
          <p className="mt-5 max-w-3xl text-sm leading-relaxed text-background-200 md:text-base">{meta.desc}</p>
        </div>
      </section>

      <section className="bg-background-50 py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="flex flex-wrap items-center gap-2">
            {tagPool.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTag(t)}
                className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors ${
                  tag === t
                    ? "bg-primary-500 font-medium text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                {t}
              </button>
            ))}
            <span className="ml-auto whitespace-nowrap text-sm text-foreground-500">
              共 {filtered.length} 部作品
            </span>
          </div>

          {filtered.length > 0 ? (
            <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {filtered.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          ) : (
            <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center">
              <i className="ri-search-line text-4xl text-foreground-400" aria-hidden="true" />
              <p className="mt-3 text-sm text-foreground-600">该标签下暂无可展示的作品，换个题材逛逛吧</p>
              <Link
                to="/"
                className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                返回首页
              </Link>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}

export default CategoryPage;